import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, BadgeCheck, ArrowUpRight, Gift } from "lucide-react";
import { usePublicSite } from "@/lib/publicSite";

// Tapping a Google review opens the live listing so visitors can read it on Google itself.
const GOOGLE_REVIEWS_URL = "https://g.page/r/CQq3-foBVx4iEAI";

const TESTIMONIALS = [
  {
    quote: "Love working with Bryan. I have a long To-Do list of house items that hasn't moved in years. Bryan is working through them quickly and with high quality.",
    name: "Pamela Ruhl",
    area: "Home to-do list",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
  {
    quote: "TOP NOTCH SERVICE!!!! 10 stars across the board. Bryan and Solid Home services did an excellent job on my home. I requested multiple jobs be taken care of and he did it professionalism and steady work. High quality work and service!! Highly recommended!",
    name: "Kristen sweet",
    area: "Bremerton · New ducting and fans",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
  {
    quote: "I needed a decaying deck railing replaced, and Bryan came out and consulted. More than just asking what I needed done he asked what my goals were, and provided options. It is his depth of knowledge that sets him apart from others. In addition to accurate quote and fast service, during the project he was able to correct other deficiencies he identified, and not only make the deck look better, but made it safer as well. Ultimately I cannot recommend him enough, I now have a long list of projects lined up for him.",
    name: "Alex B",
    area: "Deck railing replacement",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
  {
    quote: "Bryan did work for us years ago that was of the highest quality and still looks great. When we learned about his new Solid Home Services company, we quickly arranged for him to do a variety of projects around our house that were too challenging for us. He arrived on time and completed them with cheerful expertise. We recommend him highly and are looking forward to working with him again.",
    name: "Cyndi McLean",
    area: "Poulsbo",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
  {
    quote: "We hired Solid Home Services for a home job & it went very well. The set up was easy. The quote was accurate. The work was pro-level. I would & will use them again & strongly recommend this company.",
    name: "Jacob Angel",
    area: "",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
  {
    quote: "Highly recommend Solid Home Services. As first time home buyers we lacked the tools and skills to hang a louvered slab door on our utility closet. I was so happy to find a local professional able to do the job. The door install was perfect. Bryan was on time, respectful of our property, and communication was excellent from the initial consultation through completion. Pricing was fair as well. We're very happy with the result and will use Solid Home Services again.",
    name: "Kellen Bailey",
    area: "Louvered door install",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
  {
    quote: "Bryan did exceptional work at our home in fixing a problem with a pair of French doors that did not close properly. He had to completely disassemble the door frame and build it custom so that the opening would be square and plumb. It opens and closes easily now and looks great. He is a craftsman who is both skilled and easy to work with.",
    name: "Rae Holt",
    area: "French door repair",
    job: "Google review",
    link: GOOGLE_REVIEWS_URL,
  },
];

const CardBody = ({ t }) => (
  <>
    <div className="flex gap-1" aria-label="5 star rating">
      {[...Array(5)].map((_, j) => (
        <Star key={j} className="h-4 w-4 fill-terracotta text-terracotta" />
      ))}
    </div>
    <blockquote className="mt-4 text-foreground leading-relaxed flex-1">“{t.quote}”</blockquote>
    <div className="mt-6 pt-4 border-t border-border">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-bold text-navy">{t.name}</p>
          {t.area && <p className="text-xs text-muted-foreground mt-0.5">{t.area}</p>}
        </div>
        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-mono uppercase tracking-wider text-navy/70">
          <BadgeCheck className="h-4 w-4 text-terracotta" /> {t.job}
        </span>
      </div>
      {t.link && (
        <p className="mt-3 flex items-center gap-1 text-xs font-semibold text-terracotta">
          Read on Google <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </p>
      )}
    </div>
  </>
);

const ReviewCard = ({ t, index, ariaHidden, snap }) => {
  const cls =
    `bg-card border border-border p-6 flex flex-col w-[82vw] max-w-[360px] sm:w-[360px] shrink-0 hover:border-navy/40 hover:shadow-lg transition-[box-shadow,border-color] duration-300 ${snap ? "snap-start" : ""}`;
  return (
    <a
      href={t.link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaHidden ? undefined : `Read ${t.name}'s review on Google`}
      aria-hidden={ariaHidden || undefined}
      tabIndex={ariaHidden ? -1 : undefined}
      data-testid={ariaHidden ? undefined : `testimonial-card-${index}`}
      data-review-card
      className={`${cls} group cursor-pointer no-underline`}
    >
      <CardBody t={t} />
    </a>
  );
};

const useMedia = (query) => {
  const [match, setMatch] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const onChange = () => setMatch(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [query]);
  return match;
};

// "4.9 ★★★★★ · 23 Google reviews" — the numbers the owner enters in Admin → Public site.
const GoogleRatingSummary = () => {
  const google = usePublicSite()?.google;
  if (!google?.rating) return null;
  const rating = Number(google.rating);
  const full = Math.round(rating);
  return (
    <a href={google.url} target="_blank" rel="noreferrer" data-testid="reviews-google-rating"
      className="group mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 border border-border bg-card px-4 py-3 hover:border-navy/40 transition-colors">
      <span className="font-display text-3xl font-extrabold text-navy leading-none" data-testid="reviews-google-rating-value">
        {rating.toFixed(1)}
      </span>
      <span className="flex gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
        {[...Array(5)].map((_, j) => (
          <Star key={j} className={`h-5 w-5 text-terracotta ${j < full ? "fill-terracotta" : ""}`} />
        ))}
      </span>
      {google.count ? (
        <span className="text-sm font-semibold text-navy group-hover:text-terracotta transition-colors" data-testid="reviews-google-review-count">
          {google.count} Google review{google.count === 1 ? "" : "s"}
        </span>
      ) : (
        <span className="text-sm font-semibold text-navy group-hover:text-terracotta transition-colors">on Google</span>
      )}
      <ArrowUpRight className="h-4 w-4 text-terracotta transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  );
};

// Phones/tablets (and reduced motion): one copy of each review in a swipe row with dots.
const SwipeDots = ({ active, onPick }) => (
  <div className="mt-5 flex justify-center gap-1" data-testid="testimonials-dots">
    {TESTIMONIALS.map((t, i) => (
      <button key={t.name} type="button" onClick={() => onPick(i)} aria-label={`Show review ${i + 1} of ${TESTIMONIALS.length}`}
        aria-current={i === active || undefined} data-testid={`testimonial-dot-${i}`}
        className="flex h-6 w-6 items-center justify-center">
        <span className={`block h-2 rounded-full transition-[width,background-color] duration-300 ${i === active ? "w-5 bg-terracotta" : "w-2 bg-navy/25"}`} />
      </button>
    ))}
  </div>
);

export const Testimonials = () => {
  const [paused, setPaused] = useState(false);
  const [active, setActive] = useState(0);
  const rowRef = useRef(null);
  // Reduced motion, phones and touch tablets get a swipe row — each review exactly once —
  // instead of the looping marquee (which needs two back-to-back copies of every card).
  const reduced = useMedia("(prefers-reduced-motion: reduce)");
  const touch = useMedia("(max-width: 767px), (hover: none) and (pointer: coarse)");
  const swipe = reduced || touch;
  const loop = swipe ? TESTIMONIALS : [...TESTIMONIALS, ...TESTIMONIALS];

  const picked = useRef(null);
  // Scroll position that puts each card at the row's left edge.
  const targets = () => {
    const cards = [...(rowRef.current?.querySelectorAll("[data-review-card]") || [])];
    const pad = cards[0] ? parseFloat(getComputedStyle(cards[0].parentElement).paddingLeft) : 0;
    return cards.map((c) => c.offsetLeft - pad);
  };
  const onScroll = () => {
    const el = rowRef.current;
    const t = targets();
    const max = el.scrollWidth - el.clientWidth;
    if (!t.length || max <= 0) return;
    if (el.scrollLeft >= max - 1) {  // at the end several cards can't reach the left edge
      const p = picked.current;
      setActive(p != null && t[p] >= max - 1 ? p : t.length - 1);
      return;
    }
    setActive(t.reduce((best, x, i) => (Math.abs(x - el.scrollLeft) < Math.abs(t[best] - el.scrollLeft) ? i : best), 0));
  };
  const pick = (i) => {
    picked.current = i;
    setActive(i);
    rowRef.current?.scrollTo({ left: targets()[i], behavior: "smooth" });
  };

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-16 sm:py-24 lg:py-32">
      <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-10 sm:mb-14"
        >
          <p className="overline-label mb-4">Word on the street</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-navy max-w-2xl">
            North Kitsap neighbors who don't call anyone else.
          </h2>
          <GoogleRatingSummary />
        </motion.div>
      </div>

      {/* Desktop: full-bleed auto-scrolling marquee (hover to pause). Touch screens: swipe row. */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        ref={rowRef}
        onScroll={swipe ? onScroll : undefined}
        className={`testimonial-marquee relative ${swipe ? "overflow-x-auto snap-x snap-mandatory scroll-px-4 sm:scroll-px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" : "overflow-hidden"} ${paused ? "is-paused" : ""}`}
        data-testid="testimonials-marquee"
        data-mode={swipe ? "swipe" : "marquee"}
        onTouchStart={swipe ? () => { picked.current = null; } : () => setPaused(true)}
        onTouchEnd={swipe ? undefined : () => setPaused(false)}
        onTouchCancel={swipe ? undefined : () => setPaused(false)}
      >
        {!swipe && (
          <>
            {/* Fade edges so cards drift in/out gracefully */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 z-10 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 z-10 bg-gradient-to-l from-background to-transparent" />
          </>
        )}

        <div className={`${swipe ? "" : "animate-testimonial-marquee"} flex items-stretch gap-4 sm:gap-6 w-max px-4 sm:px-8`}>
          {loop.map((t, i) => (
            <ReviewCard
              key={`${t.name}-${i}`}
              t={t}
              index={i % TESTIMONIALS.length}
              ariaHidden={!swipe && i >= TESTIMONIALS.length}
              snap={swipe}
            />
          ))}
        </div>
      </motion.div>
      {swipe && <SwipeDots active={active} onPick={pick} />}
      <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl mt-8 sm:mt-10">
        <p data-testid="reviews-referral-line" className="flex items-start sm:items-center gap-2.5 text-sm sm:text-base text-navy">
          <Gift className="h-5 w-5 text-terracotta shrink-0 mt-0.5 sm:mt-0" />
          <span>
            <span className="font-semibold">Give $25, get $25:</span> share your link from your{" "}
            <Link
              to="/portal/demo"
              data-testid="reviews-referral-portal-link"
              className="font-semibold text-terracotta underline decoration-dotted underline-offset-4 hover:text-terracotta-hover transition-colors"
            >
              customer portal
            </Link>
          </span>
        </p>
      </div>
    </section>
  );
};
